import { EmployeeSchedule } from '@/helpers/workingPattern/employeeSchedule';
import { Contract } from '@/services/contract/contracts.types';
import { useEffect, useMemo, useState } from 'react';
import { dayIndexes } from '../../../../../helpers/dayIndexes';
import strings from '../../../team.translations';
import { DEFAULT_END, DEFAULT_START } from '../../splitAvailabilityDefaults';
import { buildSplitDefaults, canAddSplitPeriod, ContractBounds, getDaySchedule } from './splitAvailability';

export const useScheduleEditor = (initSchedule: EmployeeSchedule[], contract?: Contract) => {
  const bounds: ContractBounds = contract ?? {};
  const [schedule, setSchedule] = useState<EmployeeSchedule[]>(initSchedule);
  const [effectiveFrom, setEffectiveFrom] = useState<Date | undefined>(undefined);

  useEffect(() => {
    setSchedule(initSchedule);
  }, [initSchedule]);

  useEffect(() => {
    setEffectiveFrom(undefined);
  }, [initSchedule]);

  const replaceScheduleDay = (scheduleDay: EmployeeSchedule, update: Partial<EmployeeSchedule>) => {
    setSchedule(current =>
      current.map(day =>
        day.dayNumber === scheduleDay.dayNumber && day.slotNumber === scheduleDay.slotNumber
          ? { ...day, ...update }
          : day
      )
    );
  };

  const onCheck = (scheduleDay: EmployeeSchedule) => () => {
    if ((scheduleDay.slotNumber ?? 0) !== 0) {
      return;
    }

    if (scheduleDay.available) {
      setSchedule(current =>
        current
          .filter(item => !(item.dayNumber === scheduleDay.dayNumber && (item.slotNumber ?? 0) === 1))
          .map(item =>
            item.dayNumber === scheduleDay.dayNumber && (item.slotNumber ?? 0) === 0
              ? { ...item, available: false }
              : item
          )
      );
      return;
    }

    replaceScheduleDay(scheduleDay, {
      available: true,
      start: scheduleDay.start || DEFAULT_START,
      end: scheduleDay.end || DEFAULT_END
    });
  };

  const onStartChange = (scheduleDay: EmployeeSchedule) => (newVal?: string) => {
    replaceScheduleDay(scheduleDay, { start: newVal });
  };

  const onEndChange = (scheduleDay: EmployeeSchedule) => (newVal?: string) => {
    replaceScheduleDay(scheduleDay, { end: newVal });
  };

  const onAdd = (scheduleDay: EmployeeSchedule) => () => {
    setSchedule(current => {
      const currentDay = getDaySchedule(current, scheduleDay.dayNumber);

      if (currentDay.length >= 2 || !currentDay[0]?.available) {
        return current;
      }

      const splitDefaults = buildSplitDefaults(current, scheduleDay, bounds);

      if (splitDefaults.placement === 'before') {
        return current
          .flatMap(item => {
            if (item.dayNumber !== scheduleDay.dayNumber) {
              return item;
            }

            if ((item.slotNumber ?? 0) === 0) {
              return [{ ...item, slotNumber: 1 }];
            }

            return item;
          })
          .concat({
            dayNumber: scheduleDay.dayNumber,
            slotNumber: 0,
            id: '',
            day: scheduleDay.day,
            start: splitDefaults.start,
            end: splitDefaults.end,
            available: true
          });
      }

      return [
        ...current,
        {
          dayNumber: scheduleDay.dayNumber,
          slotNumber: 1,
          id: '',
          day: scheduleDay.day,
          start: splitDefaults.start,
          end: splitDefaults.end,
          available: true
        }
      ];
    });
  };

  const onDelete = (scheduleDay: EmployeeSchedule) => () => {
    setSchedule(current => {
      const remaining = current.filter(
        item => !(item.dayNumber === scheduleDay.dayNumber && item.slotNumber === scheduleDay.slotNumber)
      );
      const dayItems = getDaySchedule(remaining, scheduleDay.dayNumber);

      // If only one slot remains for the day and it is not slot 0, renumber it to slot 0.
      if (dayItems.length === 1 && (dayItems[0].slotNumber ?? 0) !== 0) {
        return remaining.map(item => (item.dayNumber === scheduleDay.dayNumber ? { ...item, slotNumber: 0 } : item));
      }

      return remaining;
    });
  };

  const validationErrors = useMemo(() => {
    const errors: string[] = [];

    dayIndexes.forEach(dayNumber => {
      const daySchedule = getDaySchedule(schedule, dayNumber);
      const activeSlots = daySchedule.filter(item => item.available);

      if (activeSlots.length === 0) {
        return;
      }

      activeSlots.forEach(slot => {
        if (!slot.start || !slot.end || slot.start >= slot.end) {
          errors.push(strings.editSchedule.invalidAvailabilityPeriod(slot.day));
        }
      });

      if (activeSlots.length > 1) {
        const firstSlot = activeSlots.find(item => (item.slotNumber ?? 0) === 0) ?? activeSlots[0];
        const secondSlot = activeSlots.find(item => (item.slotNumber ?? 0) === 1);

        if (!firstSlot || !secondSlot) {
          errors.push(strings.editSchedule.secondAvailabilityRequiresFirst(activeSlots[0].day));
          return;
        }

        if ((firstSlot.end || '') > (secondSlot.start || '')) {
          errors.push(strings.editSchedule.availabilityPeriodsCannotOverlap(firstSlot.day));
        }
      }
    });

    if (!effectiveFrom) {
      errors.push(strings.editSchedule.effectiveFromRequired);
    }

    return errors;
  }, [schedule, effectiveFrom]);

  return {
    schedule,
    effectiveFrom,
    setEffectiveFrom,
    getDaySchedule: (dayNumber?: number) => getDaySchedule(schedule, dayNumber),
    canAddSplitPeriod: (dayNumber?: number) => canAddSplitPeriod(schedule, dayNumber, bounds),
    onCheck,
    onStartChange,
    onEndChange,
    onAdd,
    onDelete,
    validationErrors
  };
};
